import { createContext, useContext, useState, ReactNode } from 'react';

export interface Toast {
  id: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  persistent?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: Toast['type'], persistent?: boolean) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  function addToast(message: string, type: Toast['type'] = 'info', persistent = false) {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const toast: Toast = { id, message, type, persistent };

    setToasts(prev => [toast, ...prev]);

    if (!persistent) {
      setTimeout(() => {
        removeToast(id);
      }, 5000);
    }
  }

  function removeToast(id: string) {
    setToasts(prev => prev.filter(t => t.id !== id));
  }

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  // Provide showToast as an alias for addToast for backwards compatibility
  return {
    ...context,
    showToast: context.addToast,
  };
}
